import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Scroll-to-top on route change (issue #209). React Router does not reset
// scroll position on navigation by itself — moving from a scrolled-down
// page to a new route left the new page wherever the old page's scrollY
// happened to be. Mounted once, as a sibling of <Routes> inside
// <BrowserRouter> in App.js, so it covers every route — including
// /login, /register, etc., which sit outside Layout — not just the ones
// nested under Layout's <Outlet/>.
//
// Keyed on `pathname` and `hash`, deliberately not the full location:
// DevelopmentPlan (and others) deep-link tab state via `?tab=...`, and
// switching tabs updates the URL's search string without changing the
// route. Resetting scroll on every location change would yank a user
// back to the top mid-tab-switch on a page they've scrolled down; leaving
// `search` out of the dependency list means only an actual route (or
// hash) change scrolls the page, matching what "scroll-to-top on
// navigation" is supposed to mean.
//
// A `#hash` on the destination is honoured, not fought: if an element
// with that id exists, we scroll to it instead of the top, so in-page
// deep links keep working.
//
// A direct load of a `#hash` URL (#1584) cannot trust the first scroll: the
// target may render after this effect has run, and content above it moves
// when the web fonts swap in or late content lands. So the hash branch keeps
// following the target for a bounded window (HASH_FOLLOW_MS): it waits for the
// element to appear, scrolls to it, and re-aligns once per layout change that
// leaves it off its mark. It stops at once on the first wheel, touch or key
// press (the user is in charge from then on), on a new route or hash, and
// when the window ends. All of that lives in `followHashTarget` below.
//
// The follow runs only after a DIRECT LOAD (the first effect run of the page):
// a later in-app navigation to a hash whose target is already on the page
// scrolls once, as it always did, and follows nothing, so a screen that
// positions itself afterwards (SettingsScreen's centring) keeps its choice.
// A target that is not there yet is still waited for on every run. A screen that
// centres the target itself marks its root `data-scroll-owner` (#1655): an in-app
// move to such a target is left to that screen, so only one scroller moves the page.
// The mark is checked at the first effect run, so it covers a target that renders in
// that commit; a target that renders later inside the screen is not covered (the mark
// is gone by then), so a screen that claims the hash must render the target at once.
const HASH_FOLLOW_MS = 2000;
const HASH_DRIFT_PX = 1;
const SMOOTH_REST_MS = 150;
const USER_INPUT_EVENTS = ["wheel", "touchstart", "touchmove", "keydown", "mousedown"];

let firstRun = true;

// Test hook: the first-run flag is module state, so each unit test starts a new "page".
export function resetDirectLoadForTests() {
  firstRun = true;
}

export function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const direct = firstRun;
    firstRun = false;
    const behavior = prefersReducedMotion() ? "auto" : "smooth";

    if (hash) {
      const id = hash.slice(1);
      const target = document.getElementById(id);
      // #1655: on an in-app move, a screen that positions the target itself marks
      // its root `data-scroll-owner`; two scrollers would race, so this one stays out.
      if (target && !direct && target.closest("[data-scroll-owner]")) return undefined;
      if (target) {
        target.scrollIntoView({ block: "start", behavior });
      } else {
        window.scrollTo({ top: 0, left: 0, behavior });
      }
      if (target && !direct) return undefined;
      return followHashTarget(id, Boolean(target), behavior === "auto");
    }

    // No hash: a screen that positions itself (`data-scroll-owner`) is not sent to the top either.
    if (document.querySelector("[data-scroll-owner]")) return undefined;
    window.scrollTo({ top: 0, left: 0, behavior });
    return undefined;
  }, [pathname, hash]);

  return null;
}

// Keeps the element `id` aligned to the top of the viewport (its own
// scroll-margin respected, which is what `scrollIntoView` does) until the
// window ends or anything else moves the page. `scrolled` says the first scroll
// already happened; `instant` says it landed at once (no smooth scroll still
// running). Returns the cleanup.
//
// The follow only re-aligns for a layout change (a font swap, late content),
// which moves the target and not scrollY. It remembers scrollY after every
// scroll it makes itself; if scrollY differs when a layout change arrives, the
// page was moved by someone else (focus, a script, find-in-page, assistive tech,
// SettingsScreen's own centring) and the follow stops instead of fighting it.
function followHashTarget(id, scrolled, instant) {
  let stopped = false;
  let lastY = scrolled && instant ? window.scrollY : null;
  const cleanups = [];
  const stop = () => {
    stopped = true;
    cleanups.forEach((fn) => fn());
  };

  const alignIfOff = () => {
    const el = document.getElementById(id);
    if (!el) return false;
    const margin = parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0;
    if (Math.abs(el.getBoundingClientRect().top - margin) > HASH_DRIFT_PX) {
      el.scrollIntoView({ block: "start", behavior: "auto" });
      lastY = window.scrollY;
    }
    return true;
  };

  USER_INPUT_EVENTS.forEach((type) => {
    window.addEventListener(type, stop, { passive: true, once: true });
    cleanups.push(() => window.removeEventListener(type, stop));
  });
  if (scrolled && !instant) trackSmoothScroll();
  const timer = window.setTimeout(stop, HASH_FOLLOW_MS);
  cleanups.push(() => window.clearTimeout(timer));

  // A smooth first scroll is running. We cannot tell its scroll events from
  // anyone else's, so until the page has been still for SMOOTH_REST_MS (the
  // whole smooth scroll, about 0.8 to 0.9 s on /privacy at 390, plus 150 ms)
  // a scroll by someone else is treated as ours and a layout change re-aligns
  // over it. When the page comes to rest, lastY is fixed and the stop applies.
  function trackSmoothScroll() {
    let rest = null;
    const onScroll = () => {
      window.clearTimeout(rest);
      rest = window.setTimeout(done, SMOOTH_REST_MS);
    };
    const done = () => {
      lastY = window.scrollY;
      window.removeEventListener("scroll", onScroll);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    rest = window.setTimeout(done, SMOOTH_REST_MS);
    cleanups.push(() => {
      window.clearTimeout(rest);
      window.removeEventListener("scroll", onScroll);
    });
  }

  const observeLayout = () => {
    if (typeof ResizeObserver !== "function") return;
    // The first notification is the observer's own start-up, not a layout
    // change: skipping it keeps the smooth first scroll from being cut short.
    let started = false;
    const resize = new ResizeObserver(() => {
      if (!started) {
        started = true;
        return;
      }
      if (stopped) return;
      if (lastY !== null && Math.abs(window.scrollY - lastY) > HASH_DRIFT_PX) {
        stop();
        return;
      }
      alignIfOff();
    });
    resize.observe(document.body);
    cleanups.push(() => resize.disconnect());
  };

  if (scrolled) {
    observeLayout();
  } else if (typeof MutationObserver === "function") {
    // The target renders after this effect: wait for it, scroll once, then follow it.
    const mutation = new MutationObserver(() => {
      if (stopped || !document.getElementById(id)) return;
      mutation.disconnect();
      alignIfOff();
      lastY = window.scrollY;
      observeLayout();
    });
    mutation.observe(document.body, { childList: true, subtree: true });
    cleanups.push(() => mutation.disconnect());
  }

  return stop;
}

function prefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
